/**
 * Deep-linking support for the Movie Browser tabs.
 *
 * A tab is identified by its label, and labels are read straight off
 * window.mapiTranslations. Those values are strings for messages that come from
 * the Movie API messages list, but arrays (["Coming Soon"]) for the ones that
 * fall through to the bundled gettext json. Tabs.jsx compares labels with !==,
 * which only works because every comparison reads the same property of the same
 * object. So never rebuild a label from its translated text - always hand back
 * window.mapiTranslations[key], exactly as App.jsx writes it in JSX.
 */

// url slug -> the mapiTranslations key used as the tab label in App.jsx
export const TAB_SLUGS = {
  'choose-film': 'Choose Film',
  'choose-cinema': 'Choose Cinema',
  'coming-soon': 'Coming Soon',
};

export const slugify = (value) => {
  if (!value) {
    return '';
  }

  return String(value)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * The tab slug requested by the current URL. The hash wins over the localized
 * ?tab= value, so an in-page anchor beats a stale query string.
 */
export const getRequestedTabSlug = (paramValue) => {
  const hash = (window.location.hash || '').replace(/^#/, '');

  return slugify(hash || paramValue || '');
}

/**
 * Resolve a slug to the label value of a rendered tab, or null when the slug is
 * unknown or points at a tab this page doesn't render.
 */
export const resolveTabLabel = (slug, options = {}) => {
  if (!slug) {
    return null;
  }

  const { groups = [], singleCinema } = options;
  const translationKey = TAB_SLUGS[slug];

  if (translationKey) {
    // the "Choose Cinema" tab is only rendered when singleCinema === '0'
    if (slug === 'choose-cinema' && singleCinema !== '0') {
      return null;
    }

    return window.mapiTranslations[translationKey];
  }

  // dynamic group tabs - matched on either their code or their name
  const group = groups.find(item => slugify(item.code) === slug || slugify(item.name) === slug);

  return group ? group.name : null;
}

// matches the theme's anchor scroll offset - see resources/js/modules/links.js
export const TAB_SCROLL_OFFSET = 70;

/**
 * Bring the tab strip into view after a deep link activates a tab. A no-op when
 * the movie browser isn't on the page.
 *
 * The target is re-measured for a short while after the first scroll: images
 * above the browser (the page hero) often load late and push the tabs down, so
 * a single measurement taken at mount lands in the wrong place. Any manual
 * scroll cancels the correction so we never fight the user.
 *
 * @return {function} cancels any pending correction
 */
export const scrollToTabs = (element) => {
  if (!element) {
    return () => {};
  }

  const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

  const scroll = () => {
    const top = Math.max(element.getBoundingClientRect().top + window.pageYOffset - TAB_SCROLL_OFFSET, 0);

    // already there - don't restart a smooth scroll for a sub-pixel difference
    if (Math.abs(window.pageYOffset - top) > 2) {
      window.scrollTo({ top, behavior });
    }
  };

  let timer = null;

  const stop = () => {
    clearTimeout(timer);
    window.removeEventListener('load', correct);
    window.removeEventListener('wheel', stop);
    window.removeEventListener('touchstart', stop);
  }

  function correct() {
    scroll();
  }

  scroll();

  timer = setTimeout(() => {
    scroll();
    stop();
  }, 800);

  window.addEventListener('load', correct);
  window.addEventListener('wheel', stop, { passive: true, once: true });
  window.addEventListener('touchstart', stop, { passive: true, once: true });

  return stop;
}
